/**
 * Hook useCampaignSegmentOptions
 * 
 * Busca todas as opções disponíveis para os filtros de segmentação de campanhas.
 * As opções de CRM são carregadas apenas se crmEnabled = true.
 * 
 * @author Sistema de Campanhas
 * @version 1.0.0
 */

import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { createLogger } from '@/lib/logger';
import { FilterOption, CONVERSATION_STATUSES, DEAL_STATUSES } from '@/types/campaign-segments';

// Logger para depuração
const logger = createLogger('CampaignSegmentOptions');

/**
 * Interface do retorno do hook
 */
export interface CampaignSegmentOptionsResult {
  // Contato
  contactTags: FilterOption[];
  contactSellers: FilterOption[];
  
  // Conversa
  conversationStatuses: FilterOption[];
  inboxes: FilterOption[];
  departments: FilterOption[];
  agents: FilterOption[];
  conversationPhases: FilterOption[];
  
  // CRM/Negócio (apenas se crmEnabled)
  dealStatuses: FilterOption[];
  dealTags: FilterOption[];
  dealPhases: FilterOption[];
  workspaces: FilterOption[];
  leadSources: FilterOption[];
  dealSellers: FilterOption[];
  
  // Meta
  isLoading: boolean;
  isCrmEnabled: boolean;
}

/**
 * Hook para buscar opções dos filtros de segmentação
 * 
 * @param crmEnabled - Se o módulo CRM está habilitado para o tenant
 */
export function useCampaignSegmentOptions(crmEnabled: boolean = false): CampaignSegmentOptionsResult {
  const { currentTenant } = useAuth();
  const tenantId = currentTenant?.id;

  // =========================================================================
  // OPÇÕES DE CONTATO
  // =========================================================================
  
  // Tags de contato
  const { data: contactTags = [], isLoading: loadingContactTags } = useQuery({
    queryKey: ['segment-contact-tags', tenantId],
    queryFn: async () => {
      if (!tenantId) return [];
      
      const { data, error } = await supabase
        .from('contact_tags')
        .select('id, name, color')
        .eq('tenant_id', tenantId)
        .order('name');
      
      if (error) {
        logger.error('Erro ao buscar tags de contato:', error);
        return [];
      }
      
      return data.map(tag => ({
        value: tag.id,
        label: tag.name,
        color: tag.color || undefined,
      })) as FilterOption[];
    },
    enabled: !!tenantId,
    staleTime: 5 * 60 * 1000,
  });

  // Vendedores de contato (crm_sellers)
  const { data: contactSellers = [], isLoading: loadingSellers } = useQuery({
    queryKey: ['segment-sellers', tenantId],
    queryFn: async () => {
      if (!tenantId) return [];
      
      const { data, error } = await supabase
        .from('crm_sellers')
        .select('id, name')
        .eq('tenant_id', tenantId)
        .eq('is_active', true)
        .order('name');
      
      if (error) {
        logger.error('Erro ao buscar vendedores:', error);
        return [];
      }
      
      return data.map(seller => ({
        value: seller.id,
        label: seller.name,
      })) as FilterOption[];
    },
    enabled: !!tenantId,
    staleTime: 5 * 60 * 1000,
  });

  // =========================================================================
  // OPÇÕES DE CONVERSA
  // =========================================================================
  
  // Inboxes ativos
  const { data: inboxes = [], isLoading: loadingInboxes } = useQuery({
    queryKey: ['segment-inboxes', tenantId],
    queryFn: async () => {
      if (!tenantId) return [];
      
      const { data, error } = await supabase
        .from('inboxes')
        .select('id, name')
        .eq('tenant_id', tenantId)
        .eq('status', 'active')
        .order('name');
      
      if (error) {
        logger.error('Erro ao buscar inboxes:', error);
        return [];
      }
      
      return data.map(inbox => ({
        value: inbox.id,
        label: inbox.name,
      })) as FilterOption[];
    },
    enabled: !!tenantId,
    staleTime: 5 * 60 * 1000,
  });

  // Departamentos
  const { data: departments = [], isLoading: loadingDepartments } = useQuery({
    queryKey: ['segment-departments', tenantId],
    queryFn: async () => {
      if (!tenantId) return [];
      
      const { data, error } = await supabase
        .from('departments')
        .select('id, name')
        .eq('tenant_id', tenantId)
        .eq('is_active', true)
        .order('name');
      
      if (error) {
        logger.error('Erro ao buscar departamentos:', error);
        return [];
      }
      
      return data.map(dept => ({
        value: dept.id,
        label: dept.name,
      })) as FilterOption[];
    },
    enabled: !!tenantId,
    staleTime: 5 * 60 * 1000,
  });

  // Agentes (profiles com role agent/admin no tenant)
  const { data: agents = [], isLoading: loadingAgents } = useQuery({
    queryKey: ['segment-agents', tenantId],
    queryFn: async () => {
      if (!tenantId) return [];
      
      // Buscar user_roles e depois os profiles correspondentes
      const { data: roles, error: rolesError } = await supabase
        .from('user_roles')
        .select('user_id')
        .eq('tenant_id', tenantId)
        .in('role', ['agent', 'tenant_admin', 'tenant_supervisor']);
      
      if (rolesError) {
        logger.error('Erro ao buscar roles de agentes:', rolesError);
        return [];
      }
      
      const userIds = [...new Set(roles.map(r => r.user_id))];
      if (userIds.length === 0) return [];
      
      const { data: profiles, error: profilesError } = await supabase
        .from('profiles')
        .select('id, full_name, email')
        .in('id', userIds);
      
      if (profilesError) {
        logger.error('Erro ao buscar profiles de agentes:', profilesError);
        return [];
      }
      
      return profiles.map(profile => ({
        value: profile.id,
        label: profile.full_name || profile.email || 'Sem nome',
      })).sort((a, b) => a.label.localeCompare(b.label)) as FilterOption[];
    },
    enabled: !!tenantId,
    staleTime: 5 * 60 * 1000,
  });

  // Fases do Kanban de conversas (tabela kanban_phases)
  const { data: conversationPhases = [], isLoading: loadingConvPhases } = useQuery({
    queryKey: ['segment-conversation-phases', tenantId],
    queryFn: async () => {
      if (!tenantId) return [];
      
      const { data, error } = await supabase
        .from('kanban_phases')
        .select('id, name, color')
        .eq('tenant_id', tenantId)
        .order('position');
      
      if (error) {
        logger.error('Erro ao buscar fases de conversa:', error);
        return [];
      }
      
      return data.map(phase => ({
        value: phase.id,
        label: phase.name,
        color: phase.color || undefined,
      })) as FilterOption[];
    },
    enabled: !!tenantId,
    staleTime: 5 * 60 * 1000,
  });

  // =========================================================================
  // OPÇÕES DE CRM (apenas se crmEnabled)
  // =========================================================================
  
  // Tags de negócio
  const { data: dealTags = [], isLoading: loadingDealTags } = useQuery({
    queryKey: ['segment-deal-tags', tenantId],
    queryFn: async () => {
      if (!tenantId) return [];
      
      const { data, error } = await supabase
        .from('crm_deal_tags')
        .select('id, name, color')
        .eq('tenant_id', tenantId)
        .order('name');
      
      if (error) {
        logger.error('Erro ao buscar tags de negócio:', error);
        return [];
      }
      
      return data.map(tag => ({
        value: tag.id,
        label: tag.name,
        color: tag.color || undefined,
      })) as FilterOption[];
    },
    enabled: !!tenantId && crmEnabled,
    staleTime: 5 * 60 * 1000,
  });

  // Fases do Pipeline de negócios
  const { data: dealPhases = [], isLoading: loadingDealPhases } = useQuery({
    queryKey: ['segment-deal-phases', tenantId],
    queryFn: async () => {
      if (!tenantId) return [];
      
      const { data, error } = await supabase
        .from('crm_pipeline_phases')
        .select('id, name, color, pipeline:crm_pipelines(name)')
        .eq('tenant_id', tenantId)
        .order('sort_order');
      
      if (error) {
        logger.error('Erro ao buscar fases de pipeline:', error);
        return [];
      }
      
      return data.map(phase => {
        const pipeline = phase.pipeline as { name: string } | null;
        return {
          value: phase.id,
          label: pipeline ? `${pipeline.name} > ${phase.name}` : phase.name,
          color: phase.color || undefined,
        };
      }) as FilterOption[];
    },
    enabled: !!tenantId && crmEnabled,
    staleTime: 5 * 60 * 1000,
  });

  // Workspaces do CRM
  const { data: workspaces = [], isLoading: loadingWorkspaces } = useQuery({
    queryKey: ['segment-workspaces', tenantId],
    queryFn: async () => {
      if (!tenantId) return [];
      
      const { data, error } = await supabase
        .from('crm_workspaces')
        .select('id, name')
        .eq('tenant_id', tenantId)
        .order('name');
      
      if (error) {
        logger.error('Erro ao buscar workspaces:', error);
        return [];
      }
      
      return data.map(ws => ({
        value: ws.id,
        label: ws.name,
      })) as FilterOption[];
    },
    enabled: !!tenantId && crmEnabled,
    staleTime: 5 * 60 * 1000,
  });

  // Origens de lead (valores distintos de crm_deals.source)
  const { data: leadSources = [], isLoading: loadingSources } = useQuery({
    queryKey: ['segment-lead-sources', tenantId],
    queryFn: async () => {
      if (!tenantId) return [];
      
      const { data, error } = await supabase
        .from('crm_deals')
        .select('source')
        .eq('tenant_id', tenantId)
        .not('source', 'is', null);
      
      if (error) {
        logger.error('Erro ao buscar origens de lead:', error);
        return [];
      }
      
      // Extrair valores únicos
      const uniqueSources = [...new Set(data.map(d => d.source).filter(Boolean))];
      
      return uniqueSources.sort().map(source => ({
        value: source!,
        label: source!,
      })) as FilterOption[];
    },
    enabled: !!tenantId && crmEnabled,
    staleTime: 5 * 60 * 1000,
  });

  // =========================================================================
  // RETORNO
  // =========================================================================
  
  const isLoading = loadingContactTags || loadingSellers || loadingInboxes || 
    loadingDepartments || loadingAgents || loadingConvPhases ||
    (crmEnabled && (loadingDealTags || loadingDealPhases || loadingWorkspaces || loadingSources));

  return {
    // Contato
    contactTags,
    contactSellers,
    
    // Conversa
    conversationStatuses: CONVERSATION_STATUSES,
    inboxes,
    departments,
    agents,
    conversationPhases,
    
    // CRM/Negócio
    dealStatuses: DEAL_STATUSES,
    dealTags,
    dealPhases,
    workspaces,
    leadSources,
    dealSellers: contactSellers, // Vendedores são os mesmos
    
    // Meta
    isLoading,
    isCrmEnabled: crmEnabled,
  };
}
